<template>
  <v-dialog :model-value="open" max-width="520" aria-labelledby="request-delivery-title" @update:model-value="(value: boolean) => !value && emit('close')">
    <div ref="root" class="flex flex-col rounded-xl bg-white shadow-xl" @keydown="onKeydown">
      <header class="border-b border-gray-200 px-5 py-4">
        <h2 id="request-delivery-title" class="m-0 text-lg font-semibold text-gray-900">Request delivery date</h2>
        <p class="m-0 mt-1 text-sm text-gray-500">{{ vendorName }} plans the pieces to plan by then, or a later date.</p>
      </header>

      <div class="flex flex-col gap-4 px-5 py-4">
        <!-- Free text with this page's POs as suggestions: a PO on another page can still be typed. -->
        <div class="flex flex-col gap-1">
          <label class="label flex flex-col gap-1">
            Purchase order
            <input
              v-model="poNumber"
              class="input h-9"
              :class="bad('poNumber')"
              :list="optionsId"
              placeholder="PO number, e.g. P11504"
              autocomplete="off"
              data-field="poNumber"
              :aria-invalid="error?.field === 'poNumber'"
              :aria-describedby="describedBy('poNumber')"
            />
          </label>
          <datalist :id="optionsId">
            <option v-for="po in options" :key="po" :value="po" />
          </datalist>
          <span v-if="error?.field === 'poNumber'" :id="errorId('poNumber')" class="text-xs text-red-700">{{ error.message }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <label class="label flex flex-col gap-1">
            Needed at viaPhoton by
            <input
              v-model="neededBy"
              type="date"
              class="input h-9"
              :class="bad('neededBy')"
              data-field="neededBy"
              :aria-invalid="error?.field === 'neededBy'"
              :aria-describedby="describedBy('neededBy')"
            />
          </label>
          <span v-if="error?.field === 'neededBy'" :id="errorId('neededBy')" class="text-xs text-red-700">{{ error.message }}</span>
        </div>
        <p class="m-0 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span v-if="neededBy">
            {{ vendorName }} ships by
            <!-- The colon is drawn by CSS: prettier would otherwise put a space before it. -->
            <b class="after:font-normal after:content-[':']">{{ fmtDay(shipByFor(neededBy, transitDays)) }}</b>
            {{ daysLabel(transitDays) }} before, the {{ vendorName }} transit time.
          </span>
          <template v-else>Pick a date to see when {{ vendorName }} has to ship.</template>
        </p>
        <div class="flex flex-col gap-1">
          <label class="label flex flex-col gap-1">
            <span>
              Note
              <span class="font-normal text-gray-500">(optional, {{ vendorName }} sees it)</span>
            </span>
            <textarea
              v-model="note"
              rows="3"
              class="input"
              :class="bad('note')"
              data-field="note"
              :aria-invalid="error?.field === 'note'"
              :aria-describedby="describedBy('note')"
            ></textarea>
          </label>
          <span v-if="error?.field === 'note'" :id="errorId('note')" class="text-xs text-red-700">{{ error.message }}</span>
        </div>
        <p v-if="serverError" class="m-0 text-sm text-red-700" role="alert">{{ serverError }}</p>
      </div>

      <footer class="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-5 py-3">
        <button type="button" class="btn-secondary" @click="emit('close')">Cancel</button>
        <button type="button" class="btn-primary" :disabled="sending || checking" @click="send">{{ sendLabel }}</button>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';

  import { useFieldError } from '@/composables/useFieldError';
  import logger from '@/services/logger';
  import { fetchPoDetail, requestDelivery } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import type { PoTrackerRow } from '@/types/poTracker';
  import { extractErrorMessage } from '@/utils/apiError';
  import { daysLabel, fmtDay, requestPoError, shipByFor, validateRequest, type FieldError } from '@/utils/poTracker';
  import { normalisePo } from '@/utils/poTrackerQuery';

  const props = defineProps<{ open: boolean; rows: PoTrackerRow[]; preset: string | null; vendorId: string; vendorName: string; transitDays: number }>();
  const emit = defineEmits<{ close: [] }>();

  const store = usePoTrackerStore();
  const root = ref<HTMLElement | null>(null);

  const poNumber = ref(props.preset ?? '');
  const neededBy = ref('');
  const note = ref('');
  const error = ref<FieldError | null>(null);
  const serverError = ref<string | null>(null);
  const sending = ref(false);

  const checking = ref(false);
  const sendLabel = computed(() => {
    if (checking.value) return 'Checking PO…';
    return sending.value ? 'Sending…' : `Send to ${props.vendorName}`;
  });

  const options = computed(() => {
    const open = props.rows.filter((row) => row.toPlan > 0 && !row.openRequest).map((row) => row.poNumber);
    return props.preset && !open.includes(props.preset) ? [props.preset, ...open] : open;
  });

  const { errorId, describedBy, bad } = useFieldError(error);
  const optionsId = `${errorId('poNumber')}-options`;

  onMounted(() => focusField(props.preset ? 'neededBy' : 'poNumber'));

  function focusField(field: string): void {
    void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus());
  }

  function onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (event.key === 'Enter' && target.tagName === 'INPUT') {
      event.preventDefault();
      void send();
    }
  }

  // A PO from this page's list is known to qualify; any other is loaded and checked first.
  async function typedPoError(po: string): Promise<string | null> {
    if (options.value.includes(po)) return null;
    checking.value = true;
    try {
      return requestPoError(await fetchPoDetail(props.vendorId, po, true));
    } catch (fetchError) {
      return extractErrorMessage(fetchError);
    } finally {
      checking.value = false;
    }
  }

  async function send(): Promise<void> {
    if (sending.value || checking.value) return;
    serverError.value = null;
    const po = normalisePo(poNumber.value);
    error.value = validateRequest({ poNumber: po, neededBy: neededBy.value, note: note.value });
    if (!error.value) {
      const poError = await typedPoError(po);
      if (poError) error.value = { field: 'poNumber', message: poError };
    }
    if (error.value) {
      focusField(error.value.field);
      return;
    }
    sending.value = true;
    try {
      const trimmed = note.value.trim();
      await requestDelivery(po, { vendorId: props.vendorId, neededBy: neededBy.value, ...(trimmed ? { note: trimmed } : {}) });
      logger.showSuccess(`Request sent to ${props.vendorName}`);
      emit('close');
      await store.afterWrite([po]);
    } catch (sendError) {
      serverError.value = extractErrorMessage(sendError);
    } finally {
      sending.value = false;
    }
  }
</script>
